import { Injectable } from '@angular/core'
import { Nullable } from '@app/models/nullable'
import { PetCoverageQuote } from '@app/models/pet-coverage-quote.model'
import { Pet } from '@app/models/pet.model'
import { Species } from '@app/models/species'
import { QuoteSessionService } from '@app/services/quote-session.service'
import { State, Action, StateContext, Selector, Store } from '@ngxs/store'
import { patch, append, updateItem, removeItem } from '@ngxs/store/operators'
import { firstValueFrom } from 'rxjs'
import { QuoteSessionAction } from '../quote-session/quote-session.actions'
import { PetAction } from './pet.actions'

export interface PetStateModel {
	selectedPetId: Nullable<string>
	pets: Pet[]
}

const defaults = {
	selectedPetId: null,
	pets: [],
}

@State<PetStateModel>({
	name: 'pet',
	defaults,
})
@Injectable()
export class PetState {
	constructor(
		private readonly store: Store,
		private readonly quoteSessionService: QuoteSessionService
	) {}

	@Action(PetAction.Load)
	load({ setState }: StateContext<PetStateModel>, { payload }: PetAction.Load) {
		if (!payload) return
		setState({ pets: payload, selectedPetId: payload[payload.length - 1]?.crmPetId ?? null })
	}

	@Action(PetAction.AddNew)
	async addNewPet({ setState }: StateContext<PetStateModel>) {
		try {
			const petId = await firstValueFrom(this.quoteSessionService.generateGuid())
			// const strippedId = petId.replace(/"/g, '')
			const strippedId = petId
			const newPet: Pet = {
				crmPetId: strippedId,
			}

			setState(
				patch<PetStateModel>({
					selectedPetId: strippedId,
					pets: append([newPet]),
				})
			)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(PetAction.Update)
	update({ setState }: StateContext<PetStateModel>, { payload }: PetAction.Update) {
		if (!payload.crmPetId) return
		setState(
			patch<PetStateModel>({
				pets: updateItem<Pet>(
					(p) => p?.crmPetId === payload.crmPetId,
					(s) => ({
						...s,
						...payload,
						removed: false,
						petCoverage: {
							...(s.petCoverage ?? ({} as PetCoverageQuote)),
							...(payload.petCoverage ?? ({} as PetCoverageQuote)),
						},
					})
				),
			})
		)
	}

	@Action(PetAction.UpdateVetVisit)
	async updateVetVisit(
		{ getState, setState, dispatch }: StateContext<PetStateModel>,
		{ payload }: PetAction.UpdateVetVisit
	) {
		try {
			const state = getState()
			const pets = state.pets.map((p) => ({ ...p, hadVetVisit: payload === 'true' }))
			setState(patch<PetStateModel>({ pets }))
			await firstValueFrom(
				dispatch(new QuoteSessionAction.Quote.Update({ hadVetVisit: payload === 'true' }))
			)

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	/**
	 * When a pet's age changes this MUST be called otherwise the backend
	 * will overwrite the age change
	 */
	@Action(PetAction.UnsetDateOfBirth)
	unsetDateOfBirth(
		{ setState }: StateContext<PetStateModel>,
		{ payload }: PetAction.UnsetDateOfBirth
	) {
		if (!payload) return
		setState(
			patch<PetStateModel>({
				pets: updateItem<Pet>(
					(p) => p?.crmPetId === payload,
					(s) => ({
						...s,
						dateOfBirth: undefined,
					})
				),
			})
		)
	}

	/**
	 * Will set the pet to removed in the pet entity collection.
	 * See commented alternative below for what should work but was
	 * mutating the object to _only_ contain `{ removed: true }`
	 */
	@Action(PetAction.Archive)
	async archivePet(
		{ getState, setState, dispatch }: StateContext<PetStateModel>,
		{ payload }: PetAction.Archive
	) {
		if (!payload) return
		setState(
			patch<PetStateModel>({
				pets: updateItem(
					(p) => p?.crmPetId === payload,
					(s) => ({ ...s, removed: true })
				),
			})
		)
		const activePets = this.store.selectSnapshot(PetState.activePets)
		if (!!activePets.length) return
		try {
			await firstValueFrom(dispatch(new PetAction.AddNew()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(PetAction.Unarchive)
	unarchivePet({ setState }: StateContext<PetStateModel>, { payload }: PetAction.Unarchive) {
		if (!payload) return
		setState(
			patch<PetStateModel>({
				pets: updateItem(
					(p) => p?.crmPetId === payload,
					(s) => ({ ...s, removed: false })
				),
			})
		)
	}

	@Action(PetAction.Delete)
	delete({ getState, setState }: StateContext<PetStateModel>, { payload }: PetAction.Delete) {
		const { pets } = getState()
		const activePets = pets.filter((p) => !p.removed)
		if (!payload || activePets.length === 1) return
		setState(
			patch<PetStateModel>({
				pets: removeItem<Pet>((p) => p!.crmPetId === payload),
			})
		)
	}

	@Action(PetAction.ResetCoverages)
	async resetCoverages({ getState, setState, dispatch }: StateContext<PetStateModel>) {
		try {
			const state = getState()
			const clearedCoveragePets: Pet[] = state.pets.map((p) => ({ ...p, petCoverage: undefined }))
			setState(patch<PetStateModel>({ pets: clearedCoveragePets }))
			await firstValueFrom(dispatch(new QuoteSessionAction.ActivatePolicyQuote()))

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	@Action(PetAction.Clear)
	clear({ setState }: StateContext<PetStateModel>) {
		setState({ ...defaults })
	}

	@Selector()
	static selectedPetId(state: PetStateModel) {
		return state.selectedPetId
	}

	@Selector()
	static activePets(state: PetStateModel) {
		return state.pets.filter((p) => !p.removed) || []
	}

	@Selector()
	static removedPets(state: PetStateModel) {
		return state.pets.filter((p) => p.removed) || []
	}

	@Selector()
	static hasDogs(state: PetStateModel) {
		return state.pets.filter((p) => !p.removed).some((p) => p.species === Species.Dog)
	}

	@Selector()
	static hasRx(state: PetStateModel) {
		return state.pets.filter((p) => !p.removed).every((p) => p.petCoverage?.drugDental)
	}

	@Selector()
	static hasExamFeeCoverage(state: PetStateModel) {
		return state.pets.filter((p) => !p.removed).every((p) => p.petCoverage?.examFeeCoverage)
	}
}
